/**
 * AdFrequencyWidget — T11 «Frequency and capacity»
 * ====================================================
 *
 * Спека: .claude/plans/2026-08-25-avz-ad-monetization-dashboards.md, T11.
 *
 * КАЛЕНДАРНАЯ вьюха (event_ts): распределение оплаченных показов на
 * вовлечённого пользователя в день. Гистограмма — одноцветная
 * последовательная шкала, за выбранный день; линия p50/p90 (+p99) — по дням
 * окна; кап показан референс-линией на обоих графиках и подписан как
 * рассчитан (p99 по всему окну — не игровая константа).
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { AdsService } from '../../../services/adsService';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ReferenceLine } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { adsLtv as adsLtvSpecs } from '@ggstat/chart-specs';

// Ряды/цвета — общие с MCP-рендером (shared/chart-specs/specs/adsLtv.json)
const FREQUENCY_SPEC = adsLtvSpecs.frequency;
const HISTOGRAM_SERIES = FREQUENCY_SPEC.histogram[0];
const QUANTILE_SERIES = FREQUENCY_SPEC.quantiles;
const CAP_COLOR = FREQUENCY_SPEC.capColor;
const COHORT_AGE_OPTIONS = FREQUENCY_SPEC.cohortAgeOptions;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtPct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

function HistogramTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium">{p.label} показов/день</p>
      <p className="text-muted-foreground">{fmtInt(p.users)} пользователей</p>
    </div>
  );
}

function AdFrequencyHistogram({ histogram, cap }) {
  const bins = histogram?.bins || [];
  if (bins.length === 0) return null;

  return (
    <div>
      <p className="text-sm font-medium mb-2">
        Гистограмма показов/пользователя за {histogram.day} · всего {fmtInt(histogram.totalUsers)} рекламных пользователей
      </p>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={bins} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="label" tick={{ fill: 'currentColor' }} className="text-xs" />
          <YAxis
            tick={{ fill: 'currentColor' }}
            className="text-xs"
            label={{ value: 'Пользователей', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<HistogramTooltip />} />
          <Bar dataKey={HISTOGRAM_SERIES.key} name={HISTOGRAM_SERIES.label} fill={HISTOGRAM_SERIES.color} isAnimationActive={false} />
          {cap != null && (
            <ReferenceLine
              x={bins.find((b) => cap >= b.from && (b.to === null || cap <= b.to))?.label}
              stroke={CAP_COLOR}
              strokeDasharray="4 4"
              label={{ value: `кап ≈ ${cap}`, position: 'top', fill: CAP_COLOR, fontSize: 11 }}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DailyQuantilesTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {fmtInt(entry.value)}
        </p>
      ))}
    </div>
  );
}

function AdFrequencyDailyChart({ points, cap }) {
  if (!points || points.length === 0) return null;

  return (
    <div>
      <p className="text-sm font-medium mb-2">Показы/пользователя в день: p50 / p90 / p99</p>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={points} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="day" tick={{ fill: 'currentColor' }} className="text-xs" />
          <YAxis tick={{ fill: 'currentColor' }} className="text-xs" />
          <Tooltip content={<DailyQuantilesTooltip />} />
          <Legend />
          {cap != null && (
            <ReferenceLine
              y={cap}
              stroke={CAP_COLOR}
              strokeDasharray="4 4"
              label={{ value: `кап ≈ ${cap}`, position: 'right', fill: CAP_COLOR, fontSize: 11 }}
            />
          )}
          {QUANTILE_SERIES.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

async function fetchAdFrequency(filters, extra, options) {
  return AdsService.get('/frequency', filters, extra, options);
}

export function AdFrequencyWidget() {
  const [cohortAge, setCohortAge] = useState('');
  const [histogramDay, setHistogramDay] = useState('');

  const frequencyData = useAdsWidgetData(
    (filters, options) =>
      fetchAdFrequency(
        filters,
        { cohortAge: cohortAge || undefined, histogramDay: histogramDay || undefined },
        options
      ),
    [cohortAge, histogramDay]
  );
  const widgetProps = useBaseWidget(frequencyData);
  const { data } = widgetProps;

  const points = useMemo(() => data?.points || [], [data]);
  const histogram = data?.histogram;
  const cap = data?.cap;

  const dayOptions = useMemo(() => points.map((p) => p.day), [points]);
  const selectedDay = histogramDay || histogram?.day || '';

  const capNote = cap
    ? `Кап ≈ ${fmtInt(cap.value)} показов/день. Метод: ${cap.method}.`
    : null;

  const lastPoint = points[points.length - 1];
  const atCapNote = lastPoint
    ? `${histogram?.day || lastPoint.day}: на потолке ${fmtInt(lastPoint.atCapUsers)} пользователей ` +
      `(${fmtPct(lastPoint.atCapShare)} рекламных пользователей дня); реклам/сессию ${lastPoint.adsPerSession.toFixed(2)}`
    : null;

  return (
    <BaseWidget
      {...widgetProps}
      title="Частота и потолок показов"
      subtitle="Оплаченные показы на вовлечённого пользователя в день — распределение, кап, реклам на сессию"
      emptyMessage="Нет данных за выбранный период"
      skeletonRows={8}
      headerAction={
        <select
          className="text-xs border rounded px-2 py-1 bg-background"
          value={cohortAge}
          onChange={(e) => setCohortAge(e.target.value)}
        >
          {COHORT_AGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      }
    >
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">День гистограммы:</span>
          <select
            className="border rounded px-2 py-1 bg-background"
            value={selectedDay}
            onChange={(e) => setHistogramDay(e.target.value)}
          >
            {dayOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <AdFrequencyHistogram histogram={histogram} cap={cap?.value} />
        <AdFrequencyDailyChart points={points} cap={cap?.value} />

        {(capNote || atCapNote) && (
          <div className="text-xs text-muted-foreground space-y-1">
            {capNote && <p>{capNote}</p>}
            {atCapNote && <p>{atCapNote}</p>}
          </div>
        )}
      </div>
    </BaseWidget>
  );
}

export default AdFrequencyWidget;
